import { _ as _assign } from './tslib.es6-c6da0ae6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { HorizontalContainer } from '@picsart/design-system/components/HorizontalContainer';
import { Skeleton } from '@picsart/design-system/components/Skeleton';
import classNames from 'classnames';
import { useTranslator } from '@picsart/rc/services/localization';
import AnalyticsService from '@picsart/rc/services/analytics';
import { LogoTextDefault } from '@picsart/design-system/foundation/logos';
import { IconCompanySymbol } from '@picsart/design-system/foundation/icons/IconCompanySymbol';
import { Button, ButtonSkinPrimary, ButtonVariants, ButtonElementTypes } from '@picsart/design-system/components/Button';
import { PrimaryColors, BackgroundColors } from '@picsart/design-system/foundation/colors';
import { u as useAuthContext } from './authContext-a01efdfb.js';
import { createUseStyles } from 'react-jss';

var useStyles$1 = createUseStyles({
  root: {
    display: 'block',
    padding: function padding(_a) {
      var mobileSmallLogoEnabled = _a.mobileSmallLogoEnabled;
      return mobileSmallLogoEnabled ? '0 10px' : 0;
    }
  },
  logo: {
    display: 'block',
    width: 104,
    height: 24,
    userSelect: 'none'
  },
  mobileLogo: {
    display: 'none',
    height: 28,
    width: 28
  },
  '@media screen and (max-width: 580px)': {
    mobileLogo: {
      display: function display(_a) {
        var mobileSmallLogoEnabled = _a.mobileSmallLogoEnabled;
        return mobileSmallLogoEnabled ? 'block' : 'none';
      }
    },
    logo: {
      display: function display(_a) {
        var mobileSmallLogoEnabled = _a.mobileSmallLogoEnabled;
        return mobileSmallLogoEnabled ? 'none' : 'block';
      }
    }
  }
}, {
  name: 'rc-header-dev-logo'
});

function Logo(_a) {
  var className = _a.className,
    _b = _a.src,
    src = _b === void 0 ? LogoTextDefault : _b,
    _c = _a.isMobile,
    isMobile = _c === void 0 ? false : _c,
    _d = _a.mobileSmallLogoEnabled,
    mobileSmallLogoEnabled = _d === void 0 ? true : _d,
    logoBehavior = _a.logoBehavior,
    onClick = _a.onClick;
  var styles = useStyles$1({
    mobileSmallLogoEnabled: mobileSmallLogoEnabled
  });
  var translate = useTranslator();
  var user = useAuthContext().user;
  var url = isMobile ? '/feed/' : user ? '/create' : '/';
  var ariaLabel = !user ? 'Picsart home' : 'Create with Picsart';
  var clickOnLogo = function clickOnLogo() {
    try {
      AnalyticsService.sendEvent('page_click', {
        screen_name: url,
        action_button: 'picsart',
        position: 1,
        component: 'header'
      });
      AnalyticsService.sendEvent('navigation_click');
    } catch (_a) {}
    if (typeof onClick === 'function') {
      onClick();
    } else {
      window.location.href = (logoBehavior === null || logoBehavior === void 0 ? void 0 : logoBehavior.logoLink) || url;
    }
  };
  return jsxs(Button, _assign({
    "data-test": "picsart-logo",
    "data-testid": "picsart-logo",
    className: classNames(styles.root, className),
    tabIndex: 0,
    "aria-label": ariaLabel,
    "pulse-name": "select-logo",
    onClick: clickOnLogo,
    skin: ButtonSkinPrimary,
    variant: ButtonVariants.Text,
    type: ButtonElementTypes.Link
  }, {
    children: [jsx("img", {
      className: styles.logo,
      src: (logoBehavior === null || logoBehavior === void 0 ? void 0 : logoBehavior.logoSrc) || src,
      alt: translate('Picsart Logo') || 'Picsart Logo',
      title: translate('Picsart') || 'Picsart'
    }), jsx(IconCompanySymbol, {
      fill: PrimaryColors.base["default"],
      "aria-hidden": true,
      className: classNames(styles.mobileLogo)
    })]
  }));
}

var useStyles = createUseStyles({
  root: {
    height: 80,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 1px 0 0 #f2f2f2'
  },
  content: function content(_a) {
    var isMobileHeader = _a.isMobileHeader;
    return {
      display: 'flex',
      justifyContent: isMobileHeader ? 'center' : 'flex-start',
      flexDirection: isMobileHeader ? 'column' : 'row',
      height: 80,
      alignItems: isMobileHeader ? 'flex-start' : 'center',
      position: 'relative'
    };
  },
  logoHolder: {
    paddingTop: 5,
    paddingBottom: 5,
    filter: 'blur(2)'
  },
  mobileLogo: {
    display: 'none',
    height: 28,
    width: 28
  },
  logo: function logo(_a) {
    var isMobileHeader = _a.isMobileHeader;
    return {
      width: 104,
      height: 34,
      marginTop: 5,
      marginLeft: isMobileHeader ? 40 : 0
    };
  },
  burgerHolder: function burgerHolder(_a) {
    var isMobileHeader = _a.isMobileHeader;
    return {
      display: isMobileHeader ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      position: 'absolute',
      top: 3,
      bottom: 0,
      margin: ['auto', 0]
    };
  },
  burger: {
    display: 'block',
    width: 24,
    height: 24,
    '& > path': {
      fill: BackgroundColors.tint2
    }
  },
  mobileAppBanner: {
    display: function display(_a) {
      var isMobileHeader = _a.isMobileHeader;
      return isMobileHeader ? 'block' : 'none';
    },
    background: BackgroundColors.tint2,
    width: '100%',
    height: 66
  },
  itemsPlaceholder: {
    display: function display(_a) {
      var isMobileHeader = _a.isMobileHeader;
      return isMobileHeader ? 'none' : 'flex';
    },
    marginLeft: 48
  },
  itemPlaceholder: {
    display: 'flex',
    height: 36,
    marginRight: 16,
    borderRadius: 8,
    width: 80
  },
  loginPlaceholder: {
    position: 'absolute',
    right: 24,
    width: 130,
    height: 36,
    borderRadius: 8
  },
  '@media screen and (max-width: 580px)': {
    mobileLogo: {
      display: 'block'
    },
    logo: {
      display: 'none'
    }
  }
}, {
  name: 'header-skeleton'
});

var HeaderSkeleton = function HeaderSkeleton(_a) {
  var isMobileHeader = _a.isMobileHeader,
    logoBehavior = _a.logoBehavior;
  var styles = useStyles({
    isMobileHeader: isMobileHeader
  });
  return jsx("div", _assign({
    className: styles.root,
    "data-testid": "header-skeleton"
  }, {
    children: jsxs(HorizontalContainer, _assign({
      className: styles.content
    }, {
      children: [jsx("div", _assign({
        "pulse-section": "header-logo",
        className: classNames(styles.logoHolder, 'logo')
      }, {
        children: jsx(Logo, {
          logoBehavior: logoBehavior
        })
      })), jsxs("div", _assign({
        className: styles.itemsPlaceholder
      }, {
        children: [jsx(Skeleton, {
          className: styles.itemPlaceholder
        }), jsx(Skeleton, {
          className: styles.itemPlaceholder
        }), jsx(Skeleton, {
          className: styles.itemPlaceholder
        }), jsx(Skeleton, {
          className: styles.itemPlaceholder
        }), jsx(Skeleton, {
          className: styles.itemPlaceholder
        }), jsx(Skeleton, {
          className: styles.itemPlaceholder
        }), jsx(Skeleton, {
          className: styles.itemPlaceholder
        })]
      })), jsx(Skeleton, {
        className: styles.loginPlaceholder
      })]
    }))
  }));
};

export { HeaderSkeleton as H, Logo as L };
//# sourceMappingURL=HeaderSkeleton-30c00961.js.map
